import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  ArrowRight, 
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Monitor,
  ShoppingCart,
  Target,
  Zap,
  MessageSquare,
  TrendingUp,
  CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.3 }
  }
};

// Floating Particles
const Particles = () => (
  <div className="particles">
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="particle"
        style={{
          left: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 20}s`,
          animationDuration: `${15 + Math.random() * 10}s`
        }}
      />
    ))}
  </div>
);

// Hero Section - Enhanced
const HeroSection = () => {
  return (
    <section 
      className="min-h-[100svh] flex items-center relative overflow-hidden pt-20 sm:pt-0"
      data-testid="hero-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#060606]">
        {/* Main gold glow */}
        <div className="absolute top-0 right-0 w-[500px] sm:w-[800px] h-[500px] sm:h-[800px] bg-[#D4AF37] rounded-full filter blur-[200px] sm:blur-[250px] opacity-[0.07]" />
        <div className="absolute bottom-0 left-1/4 w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] bg-[#D4AF37] rounded-full filter blur-[120px] sm:blur-[150px] opacity-[0.04]" />
      </div>
      
      {/* Decorative grid lines - hidden on mobile */}
      <div className="absolute inset-0 opacity-[0.03] hidden sm:block" style={{
        backgroundImage: `linear-gradient(rgba(212,175,55,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.3) 1px, transparent 1px)`,
        backgroundSize: '100px 100px'
      }} />
      
      {/* Accent lines - hidden on mobile */}
      <div className="absolute top-1/3 right-0 w-[40%] h-px bg-gradient-to-l from-[#D4AF37]/40 via-[#D4AF37]/20 to-transparent hidden lg:block" />
      <div className="absolute top-1/3 right-0 w-2 h-2 bg-[#D4AF37] hidden lg:block" style={{ transform: 'translateY(-50%)' }} />
      
      <div className="absolute bottom-1/3 left-0 w-[25%] h-px bg-gradient-to-r from-[#D4AF37]/30 to-transparent hidden lg:block" />
      <div className="absolute bottom-1/3 left-0 w-2 h-2 bg-[#D4AF37]/50 hidden lg:block" style={{ transform: 'translateY(-50%)' }} />

      {/* Floating elements - hidden on mobile */}
      <motion.div 
        className="absolute top-[20%] right-[15%] w-1 h-1 bg-[#D4AF37] rounded-full hidden lg:block"
        animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.5, 1] }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      <motion.div 
        className="absolute top-[60%] right-[25%] w-1.5 h-1.5 bg-[#D4AF37]/60 rounded-full hidden lg:block"
        animate={{ opacity: [0.2, 0.8, 0.2], scale: [1, 1.3, 1] }}
        transition={{ duration: 4, repeat: Infinity, delay: 1 }}
      />
      <motion.div 
        className="absolute top-[40%] right-[10%] w-1 h-1 bg-[#D4AF37]/40 rounded-full hidden lg:block"
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
      />

      {/* Content */}
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl relative z-10">
        <div className="max-w-3xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* Small tagline */}
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-6 sm:w-8 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-[0.6rem] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase">Stryx Web Solutions</span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-['Cormorant_Garamond'] text-white leading-[1.15] sm:leading-[1.1] mb-6 sm:mb-8"
            >
              A rendszer mögötti{" "}
              <span className="italic text-[#D4AF37]">csend</span>,
              <br />
              ami zajt csinál a világban.
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-zinc-400 text-base sm:text-lg max-w-md mb-8 sm:mb-10 font-light"
            >
              Prémium weboldalak vállalkozásoknak.
            </motion.p>
            
            <motion.div variants={fadeInUp}>
              <Link to="/kapcsolat">
                <Button className="btn-gold" data-testid="hero-cta-primary">
                  Valósítsuk meg
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Right side decorative text - hidden on mobile/tablet */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden xl:block">
        <div className="text-[#D4AF37]/10 text-[180px] font-['Cormorant_Garamond'] font-bold leading-none select-none" style={{ writingMode: 'vertical-rl' }}>
          S
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-zinc-600 text-[0.55rem] sm:text-[0.6rem] tracking-[0.2em] uppercase">Scroll</span>
        <motion.div 
          className="w-px h-5 sm:h-6 bg-gradient-to-b from-[#D4AF37]/50 to-transparent"
          animate={{ scaleY: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </motion.div>
    </section>
  );
};

// Services Section - Simple List
const ServicesPreview = () => {
  const services = [
    { num: "01", title: "Weboldalak", desc: "Céges bemutatkozók, landing page-ek", href: "/szolgaltatasok" },
    { num: "02", title: "Webshopok", desc: "E-commerce, online áruházak", href: "/szolgaltatasok" },
    { num: "03", title: "Egyedi projektek", desc: "Komplex webes megoldások", href: "/szolgaltatasok" }
  ];

  return (
    <section className="py-16 sm:py-24 bg-[#030303] border-t border-white/5" data-testid="services-preview">
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-20">
          {/* Left */}
          <motion.div 
            className="lg:col-span-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[#D4AF37] text-[0.65rem] sm:text-xs tracking-[0.2em] uppercase mb-2 sm:mb-3">Szolgáltatások</p>
            <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl text-white mb-4 sm:mb-5">
              Amiben segítek
            </h2>
            <p className="text-zinc-500 text-sm leading-relaxed mb-4 sm:mb-6">
              Minden projekt egyedi. Az igényeidre szabott megoldások.
            </p>
            <Link to="/szolgaltatasok" className="hidden sm:inline-flex">
              <span className="text-[#D4AF37] text-xs tracking-wider uppercase hover:text-white transition-colors inline-flex items-center gap-2">
                Részletek <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
              </span>
            </Link>
          </motion.div>

          {/* Right - Services */}
          <div className="lg:col-span-8 space-y-1 sm:space-y-2">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link 
                  to={service.href}
                  className="group flex items-center justify-between py-4 sm:py-5 border-b border-white/5"
                >
                  <div className="flex items-start gap-4 sm:gap-6">
                    <span className="text-[#D4AF37]/30 font-['Cormorant_Garamond'] text-lg sm:text-xl">{service.num}</span>
                    <div>
                      <h3 className="font-['Cormorant_Garamond'] text-lg sm:text-xl text-white mb-0.5 sm:mb-1 group-hover:text-[#D4AF37] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-zinc-500 text-xs sm:text-sm">{service.desc}</p>
                    </div>
                  </div>
                  
                  {/* Slide-in "Tudj meg többet" - hidden on mobile */}
                  <div className="hidden sm:flex items-center gap-2 opacity-0 translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                    <span className="text-[#D4AF37] text-xs tracking-wider uppercase whitespace-nowrap">
                      Tudj meg többet
                    </span>
                    <ArrowRight className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                  </div>
                  
                  {/* Mobile arrow */}
                  <ArrowRight className="w-4 h-4 text-zinc-600 sm:hidden" strokeWidth={1.5} />
                </Link>
              </motion.div>
            ))}
          </div>
          
          {/* Mobile CTA */}
          <div className="sm:hidden">
            <Link to="/szolgaltatasok">
              <Button className="btn-gold-outline w-full">
                Részletek
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Why Choose Section
const WhyChooseSection = () => {
  const benefits = [
    { icon: Target, title: "Konverzióközpontú", desc: "Nem csak szép, hanem eredményes oldalak." },
    { icon: Zap, title: "Gyors kivitelezés", desc: "2-8 hét alatt kész a projekted." },
    { icon: MessageSquare, title: "Közvetlen kommunikáció", desc: "Velem beszélsz, nem egy call centerrel." },
    { icon: TrendingUp, title: "Skálázható rendszer", desc: "Az oldalad együtt nő a vállalkozásoddal." },
  ];

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#050505]" data-testid="why-choose-section">
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-3 sm:mb-4">
              Miért én?
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-4 sm:mb-6">
              Nem weboldalt árulok.
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-zinc-400 text-base sm:text-lg italic-accent mb-6 sm:mb-8">
              Online ügyfélszerző rendszert építek.
            </motion.p>
            <motion.div variants={fadeInUp} className="gold-line mb-6 sm:mb-8" />
            <motion.p variants={fadeInUp} className="text-zinc-500 text-sm sm:text-base leading-relaxed mb-6 sm:mb-8">
              Minden projekt mögött egy cél áll: hogy a weboldalad ne csak szép legyen, 
              hanem valóban hozzon eredményt.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/rolam">
                <Button className="btn-gold-outline w-full sm:w-auto">
                  Ismerd meg a történetem
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6"
          >
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className="premium-card p-4 sm:p-5 md:p-6"
              >
                <benefit.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] mb-3 sm:mb-4" strokeWidth={1.5} />
                <h4 className="text-white text-sm sm:text-base font-medium mb-1 sm:mb-2">{benefit.title}</h4>
                <p className="text-zinc-500 text-xs sm:text-sm">{benefit.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// References Preview - Slider
const ReferencesPreview = () => {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const response = await axios.get(`${API}/references`);
        setReferences(response.data);
      } catch (error) {
        console.error("Error fetching references:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReferences();
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % references.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + references.length) % references.length);
  };

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#030303] overflow-hidden" data-testid="references-preview">
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8 sm:mb-12">
            <div>
              <p className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-2 sm:mb-4">Referenciák</p>
              <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl md:text-5xl text-white">
                Néhány munkám
              </h2>
            </div>
            
            {/* Navigation Arrows */}
            {!loading && references.length > 1 && (
              <div className="flex items-center gap-2 sm:gap-3 mt-4 sm:mt-0">
                <button
                  onClick={prevSlide}
                  className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border border-white/10 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-300"
                  data-testid="slider-prev"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={1.5} />
                </button>
                <button
                  onClick={nextSlide}
                  className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border border-white/10 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-300"
                  data-testid="slider-next"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={1.5} />
                </button>
              </div>
            )}
          </motion.div>

          {/* Slider */}
          {loading ? (
            <div className="aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] loading-shimmer rounded" />
          ) : (
            <motion.div variants={fadeInUp} className="relative">
              {/* Main Slide */}
              <div className="relative overflow-hidden">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="flex flex-col lg:grid lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 items-start lg:items-center"
                >
                  {/* Image */}
                  <div className="relative group cursor-pointer overflow-hidden w-full">
                    <div className="aspect-[16/10] relative bg-zinc-900 rounded-sm overflow-hidden">
                      <img 
                        src={references[currentSlide]?.image_url} 
                        alt={references[currentSlide]?.title}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#030303]/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-[#030303]/50" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="w-full lg:pl-8 mt-2 sm:mt-0">
                    <span className="text-[#D4AF37] text-[0.6rem] sm:text-[0.65rem] uppercase tracking-[0.15em] sm:tracking-[0.2em] mb-2 sm:mb-4 block">
                      {references[currentSlide]?.industry}
                    </span>
                    <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white mb-2 sm:mb-4">
                      {references[currentSlide]?.title}
                    </h3>
                    <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6 line-clamp-3 sm:line-clamp-none">
                      {references[currentSlide]?.description}
                    </p>
                    <div className="flex items-center gap-2 text-[#D4AF37]">
                      <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={1.5} />
                      <span className="text-xs sm:text-sm">{references[currentSlide]?.result}</span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Slide Indicators */}
              <div className="flex items-center justify-center gap-2 mt-6 sm:mt-10">
                {references.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      idx === currentSlide 
                        ? 'w-6 sm:w-8 bg-[#D4AF37]' 
                        : 'w-2 bg-white/20 hover:bg-white/40'
                    }`}
                    data-testid={`slider-dot-${idx}`}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* CTA */}
          <motion.div variants={fadeInUp} className="text-center mt-10 sm:mt-16">
            <Link to="/referenciak">
              <Button className="btn-gold">
                Összes referencia
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#050505] relative overflow-hidden" data-testid="cta-section">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#D4AF37] rounded-full filter blur-[100px] sm:blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-48 sm:w-64 h-48 sm:h-64 bg-[#D4AF37] rounded-full filter blur-[80px] sm:blur-[120px]" />
      </div>
      
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-4xl text-center relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p variants={fadeInUp} className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-4 sm:mb-6">
            Kezdjük el
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6">
            Készen állsz a{" "}
            <span className="italic-accent text-gold-gradient">változásra</span>?
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-zinc-400 text-sm sm:text-base md:text-lg mb-8 sm:mb-10 max-w-2xl mx-auto px-2">
            Foglalj egy ingyenes konzultációt, és beszéljük meg, hogyan segíthetek 
            a vállalkozásod online jelenlétének fejlesztésében.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/kapcsolat">
              <Button className="btn-gold">
                Ingyenes konzultáció
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Main Home Page Component
export default function HomePage() {
  return (
    <div>
      <HeroSection />
      <ServicesPreview />
      <WhyChooseSection />
      <ReferencesPreview />
      <CTASection />
    </div>
  );
}
